'use client';

import { c as _c } from "react/compiler-runtime";
import { Popover as RadixPopover } from 'radix-ui';
import React, { createContext, type ReactNode, useState } from 'react';
import type { MasterFilterContextValue, MasterFilterState } from './types';
import { MasterFilterAdvancedSelect, type MasterFilterAdvancedSelectProps } from './MasterFilterAdvancedSelect';
import { MasterFilterContent, type MasterFilterContentProps } from './MasterFilterContent';
import { MasterFilterRange, type MasterFilterRangeProps, type RangeValue } from './MasterFilterRange';
import { MasterFilterRangeSlider, type MasterFilterRangeSliderProps } from './MasterFilterRangeSlider';
import { MasterFilterToggle, type MasterFilterToggleProps } from './MasterFilterToggle';
import { MasterFilterTrigger, type MasterFilterTriggerProps } from './MasterFilterTrigger';

// Generic context default; sibling components cast it to their concrete state type.
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const MasterFilterContext = createContext<MasterFilterContextValue<any> | null>(null);
interface MasterFilterProviderProps<T extends MasterFilterState> {
  value: T;
  onChange: (value: T) => void;
  defaultValue?: T;
  children: ReactNode;
  getActiveCount?: (value: T, defaultValue?: T) => number;
  onOpenChange?: (isOpen: boolean, activeCount: number) => void;
  onReset?: (activeCount: number) => void;
  onFieldChange?: (name: string, activeCount: number) => void;
  /** Keys to exclude from active count and reset */
  excludeKeys?: (keyof T)[];
  /**
   * Keys to exclude from the count reported to the analytics callbacks. Defaults to
   * `excludeKeys`, and is ignored when `getActiveCount` is provided. Set it when a filter
   * should show in the badge but stay out of the tracked active_count.
   */
  analyticsExcludeKeys?: (keyof T)[];
}
function computeActiveCount<T extends MasterFilterState>(value: T, defaultValue?: T, excludeKeys?: (keyof T)[]): number {
  let count = 0;
  for (const key of Object.keys(value)) {
    // oxlint-disable-next-line typescript/consistent-type-assertions
    if (excludeKeys?.includes(key as keyof T)) continue;
    // oxlint-disable-next-line typescript/consistent-type-assertions
    const val = (value as Record<string, unknown>)[key];
    // oxlint-disable-next-line typescript/consistent-type-assertions
    const defaultVal = (defaultValue as Record<string, unknown> | undefined)?.[key];

    // Skip undefined values
    if (val === undefined) continue;

    // Arrays - count if non-empty and different from default
    if (Array.isArray(val)) {
      const defaultArr = Array.isArray(defaultVal) ? defaultVal : [];
      const isDifferent = val.length !== defaultArr.length || val.some((v, i) => v !== defaultArr[i]);
      if (val.length > 0 && isDifferent) {
        count++;
      }
      continue;
    }

    // Range objects - count if min or max is set
    if (typeof val === 'object' && val !== null && ('min' in val || 'max' in val)) {
      // oxlint-disable-next-line typescript/consistent-type-assertions
      const range = val as RangeValue;
      // oxlint-disable-next-line typescript/consistent-type-assertions
      const defaultRange = defaultVal as RangeValue | undefined;
      if (range.min !== undefined && range.min !== defaultRange?.min || range.max !== undefined && range.max !== defaultRange?.max) {
        count++;
      }
      continue;
    }

    // Booleans - count if true (and different from default)
    if (typeof val === 'boolean') {
      if (val !== (defaultVal ?? false)) {
        count++;
      }
      continue;
    }

    // Other values - count if different from default
    if (val !== defaultVal) {
      count++;
    }
  }
  return count;
}
function MasterFilterProvider(t0) {
  const $ = _c(44);
  const {
    value,
    onChange,
    defaultValue,
    children,
    getActiveCount: customGetActiveCount,
    onOpenChange: onOpenChangeProp,
    onReset,
    onFieldChange,
    excludeKeys,
    analyticsExcludeKeys
  } = t0;
  const [isOpen, setIsOpen] = useState(false);
  const [contentElement, setContentElement] = useState(null);
  let t1;
  if ($[0] !== customGetActiveCount || $[1] !== defaultValue) {
    t1 = (state, keys) => customGetActiveCount ? customGetActiveCount(state, defaultValue) : computeActiveCount(state, defaultValue, keys);
    $[0] = customGetActiveCount;
    $[1] = defaultValue;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  const countFor = t1;
  let t2;
  if ($[3] !== countFor || $[4] !== excludeKeys || $[5] !== value) {
    t2 = countFor(value, excludeKeys);
    $[3] = countFor;
    $[4] = excludeKeys;
    $[5] = value;
    $[6] = t2;
  } else {
    t2 = $[6];
  }
  const activeCount = t2;
  const trackedKeys = analyticsExcludeKeys ?? excludeKeys;
  let t3;
  if ($[7] !== countFor || $[8] !== onOpenChangeProp || $[9] !== trackedKeys || $[10] !== value) {
    t3 = open => {
      setIsOpen(open);
      onOpenChangeProp?.(open, countFor(value, trackedKeys));
    };
    $[7] = countFor;
    $[8] = onOpenChangeProp;
    $[9] = trackedKeys;
    $[10] = value;
    $[11] = t3;
  } else {
    t3 = $[11];
  }
  const handleOpenChange = t3;
  let t4;
  if ($[12] !== countFor || $[13] !== onChange || $[14] !== onFieldChange || $[15] !== trackedKeys || $[16] !== value) {
    t4 = (name, fieldValue) => {
      const nextValue = {
        ...value,
        [name]: fieldValue
      };
      onFieldChange?.(String(name), countFor(nextValue, trackedKeys));
      onChange(nextValue);
    };
    $[12] = countFor;
    $[13] = onChange;
    $[14] = onFieldChange;
    $[15] = trackedKeys;
    $[16] = value;
    $[17] = t4;
  } else {
    t4 = $[17];
  }
  const updateField = t4;
  let t5;
  if ($[18] !== countFor || $[19] !== defaultValue || $[20] !== excludeKeys || $[21] !== onChange || $[22] !== onReset || $[23] !== trackedKeys || $[24] !== value) {
    t5 = () => {
      onReset?.(countFor(value, trackedKeys));
      if (defaultValue) {
        const next = {
          ...defaultValue
        };
        if (excludeKeys) {
          for (const key of excludeKeys) {
            next[key] = value[key];
          }
        }
        onChange(next);
      } else {
        const cleared = {
          ...value
        } as Record<string, unknown>;
        for (const key_0 of Object.keys(cleared)) {
          if (excludeKeys?.includes(key_0 as keyof T)) {
            continue;
          }
          const val = cleared[key_0];
          if (Array.isArray(val)) {
            cleared[key_0] = [];
          } else {
            if (typeof val === "boolean") {
              cleared[key_0] = false;
            } else {
              if (typeof val === "object" && val !== null && ("min" in val || "max" in val)) {
                cleared[key_0] = {};
              }
            }
          }
        }
        onChange(cleared as T);
      }
    };
    $[18] = countFor;
    $[19] = defaultValue;
    $[20] = excludeKeys;
    $[21] = onChange;
    $[22] = onReset;
    $[23] = trackedKeys;
    $[24] = value;
    $[25] = t5;
  } else {
    t5 = $[25];
  }
  const clearAll = t5;
  let t6;
  if ($[26] !== clearAll || $[27] !== updateField) {
    t6 = {
      updateField,
      clearAll
    };
    $[26] = clearAll;
    $[27] = updateField;
    $[28] = t6;
  } else {
    t6 = $[28];
  }
  let t7;
  if ($[29] !== activeCount || $[30] !== contentElement || $[31] !== isOpen) {
    t7 = {
      activeCount,
      isOpen,
      setIsOpen,
      contentElement,
      setContentElement
    };
    $[29] = activeCount;
    $[30] = contentElement;
    $[31] = isOpen;
    $[32] = t7;
  } else {
    t7 = $[32];
  }
  let t8;
  if ($[33] !== t6 || $[34] !== t7 || $[35] !== value) {
    t8 = {
      state: value,
      actions: t6,
      meta: t7
    };
    $[33] = t6;
    $[34] = t7;
    $[35] = value;
    $[36] = t8;
  } else {
    t8 = $[36];
  }
  const contextValue = t8;
  let t9;
  if ($[37] !== children || $[38] !== handleOpenChange || $[39] !== isOpen) {
    t9 = <RadixPopover.Root open={isOpen} onOpenChange={handleOpenChange} modal={true}>{children}</RadixPopover.Root>;
    $[37] = children;
    $[38] = handleOpenChange;
    $[39] = isOpen;
    $[40] = t9;
  } else {
    t9 = $[40];
  }
  let t10;
  if ($[41] !== contextValue || $[42] !== t9) {
    t10 = <MasterFilterContext value={contextValue}>{t9}</MasterFilterContext>;
    $[41] = contextValue;
    $[42] = t9;
    $[43] = t10;
  } else {
    t10 = $[43];
  }
  return t10;
}
MasterFilterProvider.displayName = 'MasterFilterProvider';

// oxlint-disable-next-line typescript/consistent-type-assertions
export const MasterFilter = {
  Provider: MasterFilterProvider,
  Trigger: MasterFilterTrigger,
  Content: MasterFilterContent,
  AdvancedSelect: MasterFilterAdvancedSelect,
  Range: MasterFilterRange,
  RangeSlider: MasterFilterRangeSlider,
  Toggle: MasterFilterToggle
} as {
  Provider: <T extends MasterFilterState>(props: MasterFilterProviderProps<T>) => React.JSX.Element;
  Trigger: (props: MasterFilterTriggerProps) => React.JSX.Element;
  Content: (props: MasterFilterContentProps) => React.JSX.Element;
  AdvancedSelect: <TValue, TState extends MasterFilterState = MasterFilterState>(props: MasterFilterAdvancedSelectProps<TValue, TState>) => React.JSX.Element;
  Range: <TState extends MasterFilterState = MasterFilterState>(props: MasterFilterRangeProps<TState>) => React.JSX.Element;
  RangeSlider: <TState extends MasterFilterState = MasterFilterState>(props: MasterFilterRangeSliderProps<TState>) => React.JSX.Element;
  Toggle: <TState extends MasterFilterState = MasterFilterState>(props: MasterFilterToggleProps<TState>) => React.JSX.Element;
};

// Named exports for tree-shaking
export { MasterFilterProvider, computeActiveCount };